import React from 'react';
import { Link } from 'react-router-dom';
import { requesterEmailsOf, type RequesterSource } from '../../utils/changeRequester';
import { requesterPath } from '../../utils/requesterPath';

type RequesterEmailsProps = {
    /** Raw email list, or a change-like source resolved via `requesterEmailsOf`. */
    emails?: string[];
    source?: RequesterSource;
    className?: string;
    linkClassName?: string;
    empty?: React.ReactNode;
    /** Stop click bubbling (table rows that open a card). */
    stopPropagation?: boolean;
};

function normalizeEmails(emails: string[]): string[] {
    const result: string[] = [];
    emails.forEach((value) => {
        const email = String(value || '').trim();
        if (!email)
            return;
        if (!result.some((item) => item.toLowerCase() === email.toLowerCase()))
            result.push(email);
    });
    return result;
}

/** Renders one or more clickable requester emails linking to the Demandeur view. */
function RequesterEmails(props: RequesterEmailsProps) {
    const {
        emails,
        source,
        className = 'truncate text-gray-700 dark:text-gray-200',
        linkClassName = 'text-indigo-600 dark:text-indigo-200 hover:underline',
        empty = '—',
        stopPropagation = true,
    } = props;

    const list = normalizeEmails(
        emails?.length ? emails : (source ? requesterEmailsOf(source) : [])
    );

    if (!list.length)
        return <span className={className}>{empty}</span>;

    const onClick = stopPropagation
        ? (e: React.MouseEvent) => { e.stopPropagation(); }
        : undefined;

    return (
        <span className={className}>
            {list.map((email, index) => (
                <React.Fragment key={email.toLowerCase()}>
                    {index > 0 ? ', ' : null}
                    <Link
                        to={requesterPath(email)}
                        className={linkClassName}
                        title={email}
                        onClick={onClick}
                    >
                        {email}
                    </Link>
                </React.Fragment>
            ))}
        </span>
    );
}

export default RequesterEmails;
